import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  StageSortOption,
  HelpDeskTicket,
  HelpdeskViewItem,
} from "../../pages/Crm/components/HelpDesk/components/AllTickets/utils/types";
import type { HelpdeskStage } from "../../pages/Crm/components/HelpDesk/utils/types";
import type { GetHelpdeskViewsArg } from "../services/helpdeskApi";
import { helpdeskApi } from "../services/helpdeskApi";

interface StageTableFilters {
  priorityFilter: string[];
  assigneesFilter: string[];
  columnSort: Record<string, StageSortOption | null>;
  currentPage: number;
  rowsPerPage: number;
}

interface StageGridFilters {
  sortOption: StageSortOption | null;
}

export interface PendingTicketMove {
  ticket: HelpDeskTicket;
  fromStageKey: string;
  toStageKey: string;
  id: number;
}

interface HelpdeskState {
  activeView: string;
  selectedViewId: string;
  helpdeskViews: HelpdeskViewItem[] | null;
  stages: HelpdeskStage[] | null;
  tableFilters: Record<string, StageTableFilters>;
  gridFilters: Record<string, StageGridFilters>;
  newlyCreatedViewId: string | null;
  pendingTicketMove: PendingTicketMove | null;
}

export const DEFAULT_TABLE_FILTERS: StageTableFilters = {
  priorityFilter: [],
  assigneesFilter: [],
  columnSort: { updated_at: null, created_at: null },
  currentPage: 1,
  rowsPerPage: 10,
};

export const DEFAULT_GRID_FILTERS: StageGridFilters = {
  sortOption: null,
};

const initialState: HelpdeskState = {
  activeView: "grid",
  selectedViewId: "all_tickets",
  helpdeskViews: null,
  stages: null,
  tableFilters: {},
  gridFilters: {},
  newlyCreatedViewId: null,
  pendingTicketMove: null,
};

const getTableFilters = (
  state: HelpdeskState,
  stageKey: string
): StageTableFilters =>
  state.tableFilters[stageKey] ?? { ...DEFAULT_TABLE_FILTERS };

export const helpdeskSlice = createSlice({
  name: "helpdesk",
  initialState,
  reducers: {
    setHelpdeskActiveView: (state, action: PayloadAction<string>) => {
      state.activeView = action.payload;
    },
    setHelpdeskTablePriorityFilter: (
      state,
      action: PayloadAction<{ stageKey: string; priority: string[] }>
    ) => {
      const { stageKey, priority } = action.payload;
      const filters = getTableFilters(state, stageKey);
      filters.priorityFilter = priority;
      filters.currentPage = 1;
      state.tableFilters[stageKey] = filters;
    },
    setHelpdeskTableAssigneesFilter: (
      state,
      action: PayloadAction<{ stageKey: string; assignees: string[] }>
    ) => {
      const { stageKey, assignees } = action.payload;
      const filters = getTableFilters(state, stageKey);
      filters.assigneesFilter = assignees;
      filters.currentPage = 1;
      state.tableFilters[stageKey] = filters;
    },
    setHelpdeskAllStagesPriorityFilter: (
      state,
      action: PayloadAction<string[]>
    ) => {
      (state.stages ?? []).forEach((stage) => {
        const filters = getTableFilters(state, stage.key);
        filters.priorityFilter = action.payload;
        filters.currentPage = 1;
        state.tableFilters[stage.key] = filters;
      });
    },
    setHelpdeskAllStagesAssigneesFilter: (
      state,
      action: PayloadAction<string[]>
    ) => {
      (state.stages ?? []).forEach((stage) => {
        const filters = getTableFilters(state, stage.key);
        filters.assigneesFilter = action.payload;
        filters.currentPage = 1;
        state.tableFilters[stage.key] = filters;
      });
    },
    clearAllStageTableFilters: (state) => {
      state.tableFilters = {};
    },
    setHelpdeskTableColumnSort: (
      state,
      action: PayloadAction<{
        stageKey: string;
        columnSort: Record<string, StageSortOption | null>;
      }>
    ) => {
      const { stageKey, columnSort } = action.payload;
      const filters = getTableFilters(state, stageKey);
      filters.columnSort = columnSort;
      filters.currentPage = 1;
      state.tableFilters[stageKey] = filters;
    },
    setHelpdeskTableCurrentPage: (
      state,
      action: PayloadAction<{ stageKey: string; page: number }>
    ) => {
      const { stageKey, page } = action.payload;
      const filters = getTableFilters(state, stageKey);
      filters.currentPage = page;
      state.tableFilters[stageKey] = filters;
    },
    setHelpdeskTableRowsPerPage: (
      state,
      action: PayloadAction<{ stageKey: string; rowsPerPage: number }>
    ) => {
      const { stageKey, rowsPerPage } = action.payload;
      const filters = getTableFilters(state, stageKey);
      filters.rowsPerPage = rowsPerPage;
      filters.currentPage = 1;
      state.tableFilters[stageKey] = filters;
    },
    resetHelpdeskTableColumnSort: (
      state,
      action: PayloadAction<{ stageKey: string; column: string }>
    ) => {
      const { stageKey, column } = action.payload;
      const filters = getTableFilters(state, stageKey);
      filters.columnSort = { ...filters.columnSort, [column]: null };
      filters.currentPage = 1;
      state.tableFilters[stageKey] = filters;
    },
    setHelpdeskGridSortOption: (
      state,
      action: PayloadAction<{
        stageKey: string;
        sortOption: StageSortOption | null;
      }>
    ) => {
      const { stageKey, sortOption } = action.payload;
      state.gridFilters[stageKey] = { sortOption };
    },
    setSelectedViewId: (state, action: PayloadAction<string>) => {
      state.selectedViewId = action.payload;
    },
    setNewlyCreatedViewId: (state, action: PayloadAction<string | null>) => {
      state.newlyCreatedViewId = action.payload;
    },
    moveTicketBetweenStages: (
      state,
      action: PayloadAction<{
        ticket: HelpDeskTicket;
        fromStageKey: string;
        toStageKey: string;
      }>
    ) => {
      const { ticket, fromStageKey, toStageKey } = action.payload;
      state.pendingTicketMove = {
        ticket,
        fromStageKey,
        toStageKey,
        id: Date.now(),
      };
    },
    clearPendingTicketMove: (state) => {
      state.pendingTicketMove = null;
    },
    updateTicketData: (
      state,
      action: PayloadAction<{
        ticketId: number;
        updatedTicket: HelpDeskTicket;
      }>
    ) => {
      const { ticketId, updatedTicket } = action.payload;
      if (
        state.pendingTicketMove &&
        state.pendingTicketMove.ticket.ticket_id === ticketId
      ) {
        state.pendingTicketMove.ticket = updatedTicket;
      }
    },
    clearAllHelpdeskFilters: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        helpdeskApi.endpoints.getHelpdeskViews.matchFulfilled,
        (state, action) => {
          const payload = action.payload as { views?: HelpdeskViewItem[] };
          const page =
            (
              action.meta.arg as {
                originalArgs?: GetHelpdeskViewsArg;
              }
            )?.originalArgs?.page ?? 1;
          const after = payload?.views ?? [];
          const before = state.helpdeskViews ?? [];

          if (page <= 1) {
            state.helpdeskViews = [...after];
          } else {
            state.helpdeskViews = [...before, ...after];
          }
        }
      )
      .addMatcher(
        helpdeskApi.endpoints.getHelpdeskTickets.matchFulfilled,
        (state, action) => {
          const payload = action.payload as {
            stages?: HelpdeskStage[];
          };
          if (payload?.stages) {
            state.stages = payload.stages;
          }
        }
      )
      .addMatcher(
        helpdeskApi.endpoints.getHelpdeskStages.matchFulfilled,
        (state, action) => {
          const stages = action.payload as HelpdeskStage[] | undefined;
          if (stages) {
            state.stages = stages;
          }
        }
      )
      .addMatcher(
        helpdeskApi.endpoints.deleteHelpdeskView.matchFulfilled,
        (state, action) => {
          const { originalArgs: viewId } = action.meta.arg as {
            originalArgs: string;
          };
          if (!viewId || !state.helpdeskViews?.length) return;
          state.helpdeskViews = state.helpdeskViews.filter(
            (view) => view.id !== viewId
          );
        }
      );
  },
});

export const {
  setHelpdeskActiveView,
  setHelpdeskTablePriorityFilter,
  setHelpdeskTableAssigneesFilter,
  setHelpdeskAllStagesPriorityFilter,
  setHelpdeskAllStagesAssigneesFilter,
  clearAllStageTableFilters,
  setHelpdeskTableColumnSort,
  setHelpdeskTableCurrentPage,
  setHelpdeskTableRowsPerPage,
  resetHelpdeskTableColumnSort,
  setHelpdeskGridSortOption,
  setSelectedViewId,
  setNewlyCreatedViewId,
  moveTicketBetweenStages,
  clearPendingTicketMove,
  updateTicketData,
  clearAllHelpdeskFilters,
} = helpdeskSlice.actions;

export default helpdeskSlice.reducer;
